import { t } from "@lingui/core/macro";
import { Chart, setHighcharts } from "@highcharts/react";
import OriginalHighcharts from "highcharts";
import React from "react";
import "highcharts/modules/accessibility";
import "highcharts/modules/exporting";
import "highcharts/modules/offline-exporting";
import "highcharts/modules/broken-axis";
import "highcharts/modules/sankey";
import "highcharts/modules/mouse-wheel-zoom";
import "highcharts/modules/non-cartesian-zoom";
import "highcharts/modules/annotations";
import "highcharts/modules/pattern-fill";
import "highcharts/modules/export-data";
import "highcharts/modules/histogram-bellcurve";
import * as R from "ramda";
import { renderToString } from "react-dom/server";
import { DownloadOutlined } from "@ant-design/icons";

import colors from "../styles/colors";
import { TimeRange } from "../features/ems/domain";

setHighcharts(OriginalHighcharts);

// Allow attributes set by ant icons in the AST (to avoid warnings in console)
OriginalHighcharts.AST.allowedAttributes.push("data-icon");
OriginalHighcharts.AST.allowedAttributes.push("focusable");

OriginalHighcharts.setOptions({
  lang: {
    numericSymbols: undefined,
  },
});

export const chartSeriesColors: ReadonlyArray<string> = [
  colors.green[500],
  colors.amber[500],
  colors.blue[500],
  colors.darkOrange[500],
  colors.purple[500],
  colors.teal[500],
  colors.darkBlue[500],
  colors.lime[500],
  colors.violet[500],
  colors.yellow[500],
  colors.grassGreen[500],
  colors.cyan[500],
  colors.orange[500],
  colors.darkPurple[500],
  colors.turquoise[500],
];

export function Highchart({
  autoSize = false,
  options,
}: {
  autoSize?: boolean;
  options: OriginalHighcharts.Options;
}) {
  const baseOptions: OriginalHighcharts.Options = {
    credits: {
      enabled: false,
    },
    title: {
      text: undefined,
    },
    lang: {
      downloadSVG: t`Download SVG`,
      downloadJPEG: t`Download JPEG`,
      downloadPNG: t`Download PNG`,
      printChart: t`Print chart`,
      viewFullscreen: t`View in full screen`,
      contextButtonTitle: t`Download`,
    },
    legend: {
      enabled: false,
    },
    time: {
      timezone: undefined,
    },
    tooltip: {
      enabled: false,
    },
    chart: {
      backgroundColor: "none",
      style: {
        fontFamily: "Fira Sans, sans",
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
      },
      column: {
        borderRadius: 0,
      },
      pie: {
        borderRadius: 0,
      },
    },
    exporting: {
      fallbackToExportServer: false,
      buttons: {
        contextButton: {
          menuItems: ["downloadPNG", "downloadJPEG", "downloadSVG"],
          symbol: undefined,
          text: renderToString(<DownloadOutlined className="text-base text-aqua-600" />),
          useHTML: true,
        },
      },
    },
    colors: [...chartSeriesColors],
  };

  return (
    <div style={autoSize ? { position: "relative", height: "100%" } : {}}>
      <Chart
        containerProps={
          autoSize
            ? {
                style: { height: "100%", width: "100%", position: "absolute" },
              }
            : {}
        }
        options={R.mergeDeepRight(baseOptions, options) as OriginalHighcharts.Options}
      />
    </div>
  );
}

export function makeWeekendPlotbands(timeRange: TimeRange, offsetHours = 0) {
  const plotbands = [];
  const start = timeRange.start.startOf("day");
  const end = timeRange.end;
  const dayDiff = timeRange.diffInDays();
  const showWeekendPlotbands = dayDiff > 1 && dayDiff <= 31;

  if (showWeekendPlotbands) {
    for (let current = start; current.isBefore(end); current = current.add(1, "day")) {
      if (current.day() === 6 /* Saturday */ || current.day() === 0 /* Sunday */) {
        plotbands.push({
          from: current.subtract(offsetHours, "hours").valueOf(),
          to: current.add(1, "day").subtract(offsetHours, "hours").valueOf(),
          color: "rgba(120, 120, 120, 0.1)",
          zIndex: 1,
        });
      }
    }
  }

  return plotbands;
}
